import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getSalesOrderAlerts } from '../services/api';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const DEFAULT_ALERTS = {
  dispatch_breach: 0,
  pickup_breach: 0,
  delivery_breach: 0,
  grc_breach: 0,
  failed_orders: 0,
  unverified_orders: 0,
};

const ALERT_KEYS = Object.keys(DEFAULT_ALERTS);

function normalizeAlerts(data) {
  if (!data || typeof data !== 'object') return DEFAULT_ALERTS;
  const out = { ...DEFAULT_ALERTS };
  ALERT_KEYS.forEach((key) => {
    const v = data[key];
    if (typeof v === 'number' && !Number.isNaN(v)) out[key] = v;
  });
  return out;
}

const SalesOrderAlerts = () => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await getSalesOrderAlerts();
      setAlerts(normalizeAlerts(response?.data));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching sales order alerts:', error);
      setAlerts(DEFAULT_ALERTS);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchAlerts(true);
  };

  const handleAlertClick = (alertType) => {
    // Navigate to sales orders list with appropriate filters
    // Use same date scope as alerts (2025-04-01 to today) so counts match
    const params = new URLSearchParams();
    params.set('start_date', '2025-04-01');
    params.set('end_date', dayjs().format('YYYY-MM-DD'));

    switch (alertType) {
      case 'dispatch_breach':
        params.append('dispatch_breach', 'true');
        break;
      case 'pickup_breach':
        params.append('pickup_breach', 'true');
        break;
      case 'delivery_breach':
        params.append('delivery_breach', 'true');
        break;
      case 'grc_breach':
        params.append('grc_breach', 'true');
        break;
      case 'failed_orders':
        params.set('status', 'Failed');
        break;
      case 'unverified_orders':
        params.append('unverified_orders', 'true');
        break;
      default:
        break;
    }

    navigate(`/explore/sora/sales-orders?${params.toString()}`);
  };

  const formatTimestamp = (date) => {
    if (!date) return '';
    return dayjs(date).format('MMM D, h:mm A');
  };

  const isCriticalAlert = (alertType, count) => {
    // Critical alerts are those with red text (non-zero counts for error/breach types)
    const criticalTypes = [
      'dispatch_breach',
      'pickup_breach',
      'delivery_breach',
      'grc_breach',
      'failed_orders'
    ];
    
    return criticalTypes.includes(alertType) && count > 0;
  };

  const alertLabels = {
    dispatch_breach: 'Dispatch Breach',
    pickup_breach: 'Pickup Breach',
    delivery_breach: 'Delivery Breach',
    grc_breach: 'GRC Breach',
    failed_orders: 'Failed Orders',
    unverified_orders: 'Unverified Orders',
  };

  // Calculate total breach count
  const getTotalBreachCount = () => {
    if (!alerts) return 0;
    return (
      (alerts.dispatch_breach || 0) +
      (alerts.pickup_breach || 0) +
      (alerts.delivery_breach || 0) +
      (alerts.grc_breach || 0)
    );
  };

  const totalBreachCount = getTotalBreachCount();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
      <CardContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '0.9375rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'text.primary',
                mb: 0.5,
              }}>
              SALES ORDER ALERTS
            </Typography>
            {totalBreachCount > 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8125rem',
                  color: 'error.main',
                  fontWeight: 600,
                }}>
                Total Breaches: {totalBreachCount.toLocaleString()}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastUpdated && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  fontSize: '0.8125rem',
                  mr: 0.5
                }}>
                {formatTimestamp(lastUpdated)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                color: 'text.secondary',
                padding: '4px',
                '&:hover': { 
                  backgroundColor: 'action.hover',
                  color: 'text.primary'
                },
              }}>
              {refreshing ? (
                <CircularProgress size={16} />
              ) : (
                <RefreshIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Alerts List */}
        <List sx={{ p: 0 }}>
          {Object.entries(alertLabels).map(([key, label], index) => {
            const count = alerts?.[key] || 0;
            const isCritical = isCriticalAlert(key, count);

            return (
              <React.Fragment key={key}>
                <ListItem
                  disablePadding
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}>
                  <ListItemButton
                    onClick={() => handleAlertClick(key)}
                    sx={{
                      px: 3,
                      py: 1.75,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: '0.9375rem',
                            fontWeight: 400,
                            color: 'text.primary',
                            lineHeight: 1.5,
                          }}>
                          {label}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '0.9375rem',
                          fontWeight: 500,
                          color: isCritical ? 'error.main' : 'text.primary',
                          minWidth: 'fit-content',
                        }}>
                        {count.toLocaleString()}
                      </Typography>
                      <ChevronRightIcon
                        sx={{
                          fontSize: '1.125rem',
                          color: 'text.secondary',
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </ListItemButton>
                </ListItem>
                {index < Object.keys(alertLabels).length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};

export default SalesOrderAlerts;
